import React, { useState } from "react";
import SmartLink from "./SmartLink";

/**
 * Free-form content blocks that CMS pages (About, Stream, Home custom sections)
 * can add in any order. The editor lives in pages/admin/pageEditor/BlockListEditor.jsx.
 */

const BUTTON_CLASSES = {
  twitch: "btn btn-primary btn-twitch",
  youtube: "btn btn-primary btn-youtube",
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
};

export function paragraphsOf(text) {
  return String(text || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export function youtubeIdOf(value) {
  const text = String(value || "").trim();
  const match = text.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([\w-]{11})/);
  if (match) return match[1];
  return /^[\w-]{11}$/.test(text) ? text : "";
}

function Paragraphs({ text, className }) {
  return paragraphsOf(text).map((p, i) => (
    <p key={i} className={className}>
      {p.split("\n").map((line, j, lines) => (
        <React.Fragment key={j}>
          {line}
          {j < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
}

function BlockHeading({ block, as: Tag = "h2" }) {
  if (!block.heading && !block.highlight) return null;
  return (
    <Tag className="pb-heading">
      {block.heading}
      {block.highlight && <> <span className="highlight">{block.highlight}</span></>}
    </Tag>
  );
}

export function BlockButtons({ buttons, align }) {
  const visible = (buttons || []).filter((b) => b.label && b.href);
  if (!visible.length) return null;
  return (
    <div className={`pb-buttons pb-align-${align || "left"}`}>
      {visible.map((button, i) => (
        <SmartLink key={i} href={button.href} className={BUTTON_CLASSES[button.style] || BUTTON_CLASSES.primary}>
          {button.icon && <i className={button.icon} aria-hidden="true" />} {button.label}
        </SmartLink>
      ))}
    </div>
  );
}

function MaybeLink({ href, className, children }) {
  return href ? <SmartLink href={href} className={className}>{children}</SmartLink> : <div className={className}>{children}</div>;
}

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`pb-faq-item${open ? " is-open" : ""}`}>
      <button type="button" className="pb-faq-q" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>{item.question}</span>
        <i className={`fas fa-${open ? "minus" : "plus"}`} aria-hidden="true" />
      </button>
      {open && <div className="pb-faq-a"><Paragraphs text={item.answer} /></div>}
    </div>
  );
}

function renderBlock(block) {
  switch (block.type) {
    case "text":
      return (
        <div className={`pb-text pb-align-${block.align || "left"}`}>
          <BlockHeading block={block} />
          <Paragraphs text={block.body} />
        </div>
      );

    case "image": {
      if (!block.url) return null;
      const img = <img src={block.url} alt={block.alt || ""} loading="lazy" />;
      return (
        <figure className={`pb-image pb-image--${block.size || "medium"}`}>
          {block.href ? <SmartLink href={block.href}>{img}</SmartLink> : img}
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    }

    case "imageText":
      return (
        <div className={`pb-split pb-split--${block.imageSide === "right" ? "right" : "left"}`}>
          {block.image && (
            <div className={`pb-split__media pb-split__media--${block.imageSize || "medium"}`}>
              <img src={block.image} alt={block.alt || ""} loading="lazy" />
            </div>
          )}
          <div className="pb-split__body">
            <BlockHeading block={block} />
            <Paragraphs text={block.body} />
            <BlockButtons buttons={block.buttons} />
          </div>
        </div>
      );

    case "buttons":
      return (
        <div className={`pb-text pb-align-${block.align || "left"}`}>
          <BlockHeading block={block} />
          <BlockButtons buttons={block.buttons} align={block.align} />
        </div>
      );

    case "callout":
      return (
        <div className={`pb-callout pb-callout--${block.tone || "accent"}`}>
          {block.icon && <i className={`pb-callout__icon ${block.icon}`} aria-hidden="true" />}
          <div>
            {block.title && <h3>{block.title}</h3>}
            <Paragraphs text={block.text} />
            <BlockButtons buttons={block.buttons} />
          </div>
        </div>
      );

    case "quote":
      return (
        <blockquote className="pb-quote">
          <Paragraphs text={block.text} />
          {block.cite && <cite>— {block.cite}</cite>}
        </blockquote>
      );

    case "video": {
      const id = youtubeIdOf(block.youtube);
      return (
        <div className="pb-video">
          <BlockHeading block={block} />
          {id && (
            <div className="video-embed-container">
              <iframe
                src={`https://www.youtube.com/embed/${id}?rel=0`}
                title={block.title || block.heading || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          )}
          {block.caption && <p className="pb-caption">{block.caption}</p>}
        </div>
      );
    }

    case "cards":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          {block.intro && <p className="pb-intro">{block.intro}</p>}
          <div className={`pb-cards pb-cols-${block.columns || 3}`}>
            {(block.items || []).map((item, i) => (
              <MaybeLink key={i} href={item.href} className="pb-card">
                {item.image && <img className="pb-card__img" src={item.image} alt="" loading="lazy" />}
                {!item.image && item.icon && <i className={`pb-card__icon ${item.icon}`} aria-hidden="true" />}
                {item.title && <h3>{item.title}</h3>}
                <Paragraphs text={item.text} />
              </MaybeLink>
            ))}
          </div>
        </div>
      );

    case "stats":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          <div className="pb-stats">
            {(block.items || []).map((item, i) => (
              <div key={i} className="pb-stat">
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "list":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          {block.intro && <p className="pb-intro">{block.intro}</p>}
          <ul className="pb-list">
            {(block.items || []).filter((item) => item.text).map((item, i) => (
              <li key={i}>
                <i className={item.icon || "fas fa-check"} aria-hidden="true" />
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case "timeline":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          <ol className="pb-timeline">
            {(block.items || []).map((item, i) => (
              <li key={i}>
                {item.date && <span className="pb-timeline__date font-mono">{item.date}</span>}
                {item.title && <h3>{item.title}</h3>}
                <Paragraphs text={item.text} />
              </li>
            ))}
          </ol>
        </div>
      );

    case "faq":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          <div className="pb-faq">
            {(block.items || []).filter((item) => item.question).map((item, i) => <FaqItem key={i} item={item} />)}
          </div>
        </div>
      );

    case "gallery":
      return (
        <div className="pb-cards-wrap">
          <BlockHeading block={block} />
          <div className={`pb-gallery pb-cols-${block.columns || 3}`}>
            {(block.images || []).filter((img) => img.url).map((img, i) => (
              <figure key={i}>
                <a href={img.url} target="_blank" rel="noopener noreferrer">
                  <img src={img.url} alt={img.caption || ""} loading="lazy" />
                </a>
                {img.caption && <figcaption>{img.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      );

    case "divider":
      return block.style === "space" ? <div className="pb-spacer" aria-hidden="true" /> : <hr className="pb-divider" />;

    default:
      return null;
  }
}

export default function PageBlocks({ blocks, className = "" }) {
  const visible = (blocks || []).filter((block) => block && !block.hidden);
  if (!visible.length) return null;
  return (
    <div className={`page-blocks ${className}`}>
      {visible.map((block, i) => {
        const content = renderBlock(block);
        return content ? (
          <div key={block.id || i} className={`pb-block pb-block--${block.type}`}>
            {content}
          </div>
        ) : null;
      })}
    </div>
  );
}
